// The top-down yard plan (07 Setup), drawn in code from a spot list — there is
// no art for it (docs/ui/README.md: "The plan view is drawn in code (SVG), not
// art").
//
// The shade model is the mockup's own (docs/ui/mockups/source/07-setup.dc.html,
// `shaded()` and the AIR table): the sun moves across the plan with the hour, a
// spot's shade is a geometry test against the wall, the coop and the tree, and a
// waterer in the sun runs about 10°F hotter than one in the shade — which is
// why sun-heated water means birds drink less and lay less (REFOCUS §7).
//
// The plan is built once and the hour only moves the shade: dragging the slider
// never rebuilds the screen.

import { t } from '../i18n';
import { h } from './dom';
import {
  PLAN_VIEWBOX,
  placeholderAirTemp,
  type PlanSpot,
} from './placeholders';

const SVG_NS = 'http://www.w3.org/2000/svg';

export type PlanLayer = 'shade' | 'boss' | 'rodents';

export interface PlanSpotView {
  spot: PlanSpot;
  shaded: boolean;
  /** Water temperature in this spot, for a waterer. */
  waterTemp: number | null;
  /** i18n key for the note under the name. */
  noteKey: string | null;
  noteParams: Record<string, string | number>;
  /** A second clause for the same line, e.g. "out of boss hen view". */
  extraKey: string | null;
  /** 'shade' or 'sun', the chip on the row. */
  light: 'shade' | 'sun';
  tone: 'good' | 'warn' | 'danger' | 'plain';
}

export interface PlanState {
  hour: number;
  layers: Record<PlanLayer, boolean>;
}

/** Sun geometry, straight out of the mockup: 6am on the left, 6pm on the right. */
function sunX(hour: number): number {
  return 20 + (hour - 6) * 26.5;
}

function coopShade(hour: number): { x: number; y: number; w: number } {
  const dx = (hour - 12) * 12;
  return { x: Math.min(130, 130 + dx), y: 26, w: 110 + Math.abs(dx) };
}

function wallShade(hour: number): number {
  const dx = (hour - 12) * 12;
  return dx > 0 ? Math.round(dx * 1.6) : 0;
}

function treeShade(hour: number): { x: number; y: number } {
  return { x: 60 + (hour - 12) * 12 * 0.8, y: 230 };
}

/** The mockup's own point-in-shade test, over the same shapes. */
export function isShaded(x: number, y: number, hour: number): boolean {
  const inRect = (px: number, py: number, rx: number, ry: number, rw: number, rh: number): boolean =>
    px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
  const inCircle = (px: number, py: number, cx: number, cy: number, r: number): boolean => {
    const a = px - cx;
    const b = py - cy;
    return a * a + b * b <= r * r;
  };
  const wall = wallShade(hour);
  const coop = coopShade(hour);
  const tree = treeShade(hour);
  return (
    inRect(x, y, 14, 0, wall, PLAN_VIEWBOX.height) ||
    inRect(x, y, coop.x, coop.y, coop.w, 72) ||
    inCircle(x, y, tree.x, tree.y, 40) ||
    inCircle(x, y, 60, 222, 40)
  );
}

/**
 * What the plan knows about one spot at one hour: whether it is shaded, and for
 * a waterer how hot its water gets.
 *
 * `air` is the forecast's air at that hour, passed in rather than read here:
 * the Setup screen's temperature label and a waterer's temperature have to come
 * from the same number, and a league's air comes from the season (sim/forecast)
 * while Normal still falls back to the mockup's own day.
 */
export function planSpotView(spot: PlanSpot, hour: number, air = placeholderAirTemp(hour)): PlanSpotView {
  const shaded = isShaded(spot.x, spot.y, hour);
  // A spot behind the coop is out of the boss hen's sightline, which is its own
  // problem: a low-rank bird will not go there (REFOCUS §7).
  const outOfView = !inBossView(spot.x, spot.y);
  if (spot.hasWater) {
    // Sun-heated water: about 10°F above ambient, 2°F below in the shade. Above
    // 95°F a hen drinks much less and lays less (REFOCUS §7).
    const waterTemp = shaded ? air - 2 : air + 10;
    const noteKey =
      waterTemp <= 85
        ? 'setup.waterNormal'
        : waterTemp <= 95
          ? 'setup.waterDrinkingLess'
          : 'setup.waterDrinkingMuchLess';
    return {
      spot,
      shaded,
      waterTemp,
      noteKey,
      noteParams: { temp: waterTemp },
      extraKey: outOfView ? 'setup.outOfViewSuffix' : null,
      light: shaded ? 'shade' : 'sun',
      tone: spot.rodent ? 'danger' : waterTemp <= 85 ? 'good' : waterTemp <= 95 ? 'warn' : 'danger',
    };
  }
  return {
    spot,
    shaded,
    waterTemp: null,
    noteKey: spot.extraKey,
    noteParams: {},
    extraKey: null,
    light: shaded ? 'shade' : 'sun',
    tone: spot.rodent ? 'danger' : 'plain',
  };
}

export interface PlanView {
  readonly element: HTMLElement;
  /** Moves the shade. Nothing else in the screen is rebuilt. */
  setHour(hour: number): void;
  setLayers(layers: Record<PlanLayer, boolean>): void;
  /** The per-spot rows, for the caller to keep in step with the hour. */
  views(): PlanSpotView[];
  hour: number;
}

function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attrs)) node.setAttribute(name, String(value));
  return node;
}

/**
 * The plan itself. The shade layers are separate nodes kept in a map, so an
 * hour change is a handful of attribute writes rather than a new tree.
 */
export function createPlanView(
  spots: PlanSpot[],
  airAt: (hour: number) => number = placeholderAirTemp,
): PlanView {
  let hour = 14;
  let layers: Record<PlanLayer, boolean> = { shade: true, boss: true, rodents: true };

  const rodentZone = svg('rect', {
    x: 300, y: 108, width: 40, height: 150, fill: 'var(--cf-danger)', 'fill-opacity': 0.18,
  });
  const wallShadeRect = svg('rect', { x: 14, y: 0, width: 0, height: PLAN_VIEWBOX.height, fill: 'var(--cf-ink)', 'fill-opacity': 0.22 });
  const coopShadeRect = svg('rect', { x: 130, y: 26, width: 110, height: 72, fill: 'var(--cf-ink)', 'fill-opacity': 0.22 });
  const treeShadeCircle = svg('circle', { cx: 60, cy: 230, r: 40, fill: 'var(--cf-ink)', 'fill-opacity': 0.22 });
  const bossRight = svg('polygon', { points: '185,190 338,120 338,258', fill: 'var(--cf-warn-line)', 'fill-opacity': 0.28 });
  const bossLeft = svg('polygon', { points: '185,190 22,112 22,200', fill: 'var(--cf-warn-line)', 'fill-opacity': 0.28 });
  const sun = svg('circle', { cx: sunX(hour), cy: 9, r: 7, fill: 'var(--cf-sun)', stroke: 'var(--cf-warn)', 'stroke-width': 2 });

  const markerNodes = spots.map((spot) => {
    const group = svg('g');
    const ring = svg('circle', {
      cx: spot.x,
      cy: spot.y,
      r: 13,
      fill: spot.rodent ? 'var(--cf-danger)' : 'var(--cf-ink)',
      stroke: 'var(--cf-white)',
      'stroke-width': 2,
    });
    const text = svg('text', {
      x: spot.x,
      y: spot.y + 5,
      'text-anchor': 'middle',
      'font-size': 13,
      'font-weight': 800,
      fill: 'var(--cf-white)',
    });
    text.textContent = String(spot.n);
    group.append(ring, text);
    return { group, ring, text };
  });

  const canvas = svg('svg', {
    viewBox: `0 0 ${PLAN_VIEWBOX.width} ${PLAN_VIEWBOX.height}`,
    width: PLAN_VIEWBOX.width,
    height: PLAN_VIEWBOX.height,
    role: 'img',
    'aria-label': t('setup.planAlt'),
  });
  canvas.append(
    svg('rect', { x: 0, y: 0, width: PLAN_VIEWBOX.width, height: PLAN_VIEWBOX.height, fill: 'var(--cf-grass)' }),
    svg('rect', { x: 0, y: 0, width: 14, height: PLAN_VIEWBOX.height, fill: 'var(--cf-wall)' }),
    rodentZone,
    wallShadeRect,
    coopShadeRect,
    treeShadeCircle,
    svg('rect', {
      x: 20, y: 108, width: 320, height: 150, fill: 'none',
      stroke: 'var(--cf-line-dark)', 'stroke-width': 3, 'stroke-dasharray': '6 5',
    }),
    svg('rect', { x: 130, y: 18, width: 110, height: 70, rx: 6, fill: 'var(--cf-clay)', stroke: 'var(--cf-clay-dark)', 'stroke-width': 3 }),
    svg('line', { x1: 130, y1: 53, x2: 240, y2: 53, stroke: 'var(--cf-clay-dark)', 'stroke-width': 2 }),
    svg('rect', { x: 176, y: 86, width: 18, height: 10, fill: 'var(--cf-line-dark)' }),
    svg('circle', { cx: 60, cy: 222, r: 40, fill: 'var(--cf-sage)', 'fill-opacity': 0.75 }),
    bossRight,
    bossLeft,
    ...markerNodes.map((node) => node.group),
    label('COOP', 185, 44, 'var(--cf-white)'),
    label('MANGO', 22, 252, 'var(--cf-ink)'),
    label('RUN', 230, 252, 'var(--cf-ink)'),
    label('NEIGHBOR WALL', 18, 12, 'var(--cf-ink)'),
    sun,
  );

  const element = h('div', { class: 'cf-plan' }, canvas);
  const view: PlanView = {
    element,
    get hour() {
      return hour;
    },
    views: () => spots.map((spot) => planSpotView(spot, hour, airAt(hour))),
    setHour(next: number): void {
      hour = next;
      const wall = wallShade(hour);
      const coop = coopShade(hour);
      const tree = treeShade(hour);
      wallShadeRect.setAttribute('width', String(wall));
      coopShadeRect.setAttribute('x', String(coop.x));
      coopShadeRect.setAttribute('width', String(coop.w));
      treeShadeCircle.setAttribute('cx', String(tree.x));
      treeShadeCircle.setAttribute('cy', String(tree.y));
      sun.setAttribute('cx', String(sunX(hour)));
      // A spot the boss hen cannot see is drawn hollow with a dark number, so
      // the sightline layer changes the plan and not only the tint — and the
      // number stays readable either way.
      for (const [index, spot] of spots.entries()) {
        const node = markerNodes[index];
        const seen = inBossView(spot.x, spot.y);
        node.ring.setAttribute('fill', seen ? (spot.rodent ? 'var(--cf-danger)' : 'var(--cf-ink)') : 'var(--cf-card)');
        node.ring.setAttribute('stroke', seen ? 'var(--cf-white)' : 'var(--cf-ink)');
        node.text.setAttribute('fill', seen ? 'var(--cf-white)' : 'var(--cf-ink)');
      }
    },
    setLayers(next: Record<PlanLayer, boolean>): void {
      layers = next;
      wallShadeRect.style.display = layers.shade ? '' : 'none';
      coopShadeRect.style.display = layers.shade ? '' : 'none';
      treeShadeCircle.style.display = layers.shade ? '' : 'none';
      bossRight.style.display = layers.boss ? '' : 'none';
      bossLeft.style.display = layers.boss ? '' : 'none';
      rodentZone.style.display = layers.rodents ? '' : 'none';
    },
  };
  view.setHour(hour);
  view.setLayers(layers);
  return view;
}

function label(text: string, x: number, y: number, fill: string): SVGTextElement {
  const node = svg('text', { x, y, 'font-size': 12, 'font-weight': 800, fill });
  node.textContent = text;
  return node;
}

/** Inside the boss hen's two sightlines, or out of them. */
export function inBossView(x: number, y: number): boolean {
  const inTriangle = (
    px: number,
    py: number,
    ax: number, ay: number, bx: number, by: number, cx: number, cy: number,
  ): boolean => {
    const sign = (p1x: number, p1y: number, p2x: number, p2y: number, p3x: number, p3y: number) =>
      (p1x - p3x) * (p2y - p3y) - (p2x - p3x) * (p1y - p3y);
    const d1 = sign(px, py, ax, ay, bx, by);
    const d2 = sign(px, py, bx, by, cx, cy);
    const d3 = sign(px, py, cx, cy, ax, ay);
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
    return !(hasNeg && hasPos);
  };
  return (
    inTriangle(x, y, 185, 190, 338, 120, 338, 258) ||
    inTriangle(x, y, 185, 190, 22, 112, 22, 200)
  );
}
